import {CommonModule} from '@angular/common';
import {Component, ElementRef, Inject, ViewChild} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {MatButtonModule} from '@angular/material/button';
import {MatDialogModule, MatDialogRef, MAT_DIALOG_DATA} from '@angular/material/dialog';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {MatSelectModule} from '@angular/material/select';
import {HttpErrorResponse} from '@angular/common/http';
import {
  InfringementService,
  SendMailPayload,
  WarningEmailTemplate
} from '../../../services/infringement.service';
import {StatusMessageService} from '../../../services/status-message.service';

export interface WarningEmailDialogData {
  infringementId: string;
  recipient: string;
  sender: string;
  template: WarningEmailTemplate;
  attachments: string[];
  dialogTitle?: string;
}

@Component({
  selector: 'app-infringement-warning-email-dialog',
  standalone: true,
  templateUrl: './infringement-warning-email-dialog.component.html',
  styleUrl: './infringement-warning-email-dialog.component.scss',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule
  ]
})
export class InfringementWarningEmailDialogComponent {
  form: FormGroup;
  isSending = false;
  isTextEditable = false;
  private readonly originalHtml: string;
  private readonly originalSubject: string;

  @ViewChild('htmlEditor')
  set htmlEditor(ref: ElementRef<HTMLDivElement> | undefined) {
    if (ref && this.isTextEditable) {
      ref.nativeElement.innerHTML = this.form.controls['html'].value ?? '';
    }
  }

  constructor(
    private fb: FormBuilder,
    private infringementService: InfringementService,
    private statusMessage: StatusMessageService,
    private dialogRef: MatDialogRef<InfringementWarningEmailDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: WarningEmailDialogData
  ) {
    this.originalHtml = data.template.html ?? '';
    this.originalSubject = data.template.subject ?? '';
    this.form = this.fb.group({
      from: [{value: data.sender, disabled: true}],
      to: [{value: data.recipient, disabled: true}],
      subject: [{value: data.template.subject, disabled: true}],
      html: [{value: data.template.html ?? '', disabled: true}],
      attachments: [[] as string[]]
    });
  }

  get dialogTitle(): string {
    return this.data.dialogTitle ?? 'Warning Email';
  }

  get previewHtml(): string {
    return this.form.controls['html'].value ?? '';
  }

  enableTextEdit(): void {
    if (this.isTextEditable) {
      return;
    }

    this.isTextEditable = true;
    const htmlControl = this.form.controls['html'];
    const subjectControl = this.form.controls['subject'];
    htmlControl.enable({emitEvent: false});
    htmlControl.markAsPristine();
    subjectControl.enable({emitEvent: false});
    subjectControl.markAsPristine();
  }

  onEditorInput(event: Event): void {
    const value = (event.target as HTMLElement).innerHTML ?? '';
    this.form.controls['html'].setValue(value, {emitEvent: false});
    this.form.controls['html'].markAsDirty();
  }

  send(): void {
    this.isSending = true;
    const raw = this.form.getRawValue();
    const htmlControl = this.form.controls['html'];
    const subjectControl = this.form.controls['subject'];

    const currentHtml = raw.html ?? '';
    const currentSubject = raw.subject ?? '';

    const htmlChanged = this.isTextEditable
      && htmlControl.dirty
      && this.normalizeHtml(currentHtml) !== this.normalizeHtml(this.originalHtml);

    const subjectChanged = this.isTextEditable
      && subjectControl.dirty
      && currentSubject.trim() !== this.originalSubject.trim();

    const contentEdited = htmlChanged || subjectChanged;

    const payload: SendMailPayload = {
      identifier: this.data.infringementId,
      attachmentFileNames: raw.attachments?.length ? raw.attachments : undefined,
    };

    // If user edited content, include body and subject so backend uses them directly
    if (contentEdited) {
      payload.subject = currentSubject;
      payload.body = currentHtml;
    }

    this.infringementService.sendMail(payload).subscribe({
      next: () => this.dialogRef.close(true),
      error: (err: HttpErrorResponse) => {
        this.isSending = false;
        if (err.status === 409 && err.error?.code === 'MAIL_ALREADY_SENT') {
          this.statusMessage.showStatusMessage('This email has already been sent.', 'error');
          this.dialogRef.close('already_sent');
        } else {
          this.statusMessage.showStatusMessage('Email could not be sent!', 'error');
        }
      }
    });
  }

  private normalizeHtml(value: string | null | undefined): string {
    if (!value) {
      return '';
    }

    const doc = new DOMParser().parseFromString(value, 'text/html');
    return doc.body.innerHTML
      .replace(/<p><br><\/p>/gi, '')
      .replace(/\u00a0/g, ' ')
      .replace(/>\s+</g, '><')
      .trim();
  }
}
